import {collection, CollectionReference, doc, getDoc, getFirestore, setDoc} from "firebase/firestore"
import {ClimbingEventSimplifiedResults, UserSumOfPoints} from "../../models/ClimbingEventSimplifiedResults"
import {DifficultyCategory, isDone, UserClimbingEvent} from "../../models/UserClimbingEvent"
import {Sex} from "../../models/UserData"
import {PositionForSumOfPoints, SimplifiedResultsForUser} from "../../models/valueObjects/SimplifiedResultsForUser"
import {EventSimplifiedResultConverter} from "./converters/EventSimplifiedResultConverter"
import {updateRoutesToNrOfClimbes} from "./EventService"
import {calculateAndUpdateRouteResults} from "./EventRoutesResultService";


const converter = new EventSimplifiedResultConverter()

const getDocumentReference = (eventId: string): CollectionReference<ClimbingEventSimplifiedResults> => collection(getFirestore(), `climbingEvents/${eventId}/simplifiedResults`).withConverter(converter)

export const calculateAndUpdateSimplifiedResults = (climbingEvents: UserClimbingEvent[]): ClimbingEventSimplifiedResults[] => {
  if (climbingEvents.length <= 0) {
    return []
  }
  let simplifiedResults = Object.values(Sex).map(s => getSimplifiedResultsForSex(climbingEvents, s))
  const eventId = climbingEvents[0].eventId

  updateRoutesToNrOfClimbes(eventId, getNumberOfDonesPerRoute(climbingEvents))
  calculateAndUpdateRouteResults(climbingEvents)
  simplifiedResults.forEach(r => {
    const documentRef = doc(getDocumentReference(eventId), r.sex.toString())
    setDoc(documentRef, r)
  })
  return simplifiedResults
}

export const getSimplifiedResults = async (eventId: string): Promise<ClimbingEventSimplifiedResults[]> => {
  let results: ClimbingEventSimplifiedResults[] = []
  for await (const sex of Object.values(Sex)) {
    const documentRef = doc(getDocumentReference(eventId), sex.toString())
    let document = (await getDoc(documentRef)).data()
    if (document) {
      results.push(document)
    }
  }
  return results
}
export const getSimplifiedResultsFor = async (eventId: string, sex: Sex): Promise<ClimbingEventSimplifiedResults> => {
  const documentRef = doc(getDocumentReference(eventId), sex)
  const document = await getDoc(documentRef);
  if (document.exists()) {
    return document.data()
  } else {
    throw new Error(`nie ma aktualnie żadnych danych dla tego eventu `);
  }

}

export const getSimplifiedResultsForUser = async (eventId: string, userId: string, isSection: boolean, difficulty: DifficultyCategory, sex: Sex, currentUserPoints: number): Promise<SimplifiedResultsForUser> => {
  const results = await getSimplifiedResultsFor(eventId, sex)

  const nonSectionResultsPerUser = Array.from(results.userSumOfPoints.values())
    .filter(u => u.difficultyCategory === difficulty)

  const sectionResultsPerUser = isSection ? Array.from(results.userSumOfPoints.values())
    .filter(u => u.section) : []
  return new SimplifiedResultsForUser(sortAndMapToPosition(nonSectionResultsPerUser, userId, currentUserPoints), sortAndMapToPosition(sectionResultsPerUser, userId, currentUserPoints))
}

const getSimplifiedResultsForSex = (climbingEvents: UserClimbingEvent[], sex: Sex): ClimbingEventSimplifiedResults => {
  const results = new ClimbingEventSimplifiedResults(sex)
  climbingEvents.filter(ce => ce.sex && ce.category)
    .filter(ce => ce.sex === sex)
    .map<UserSumOfPoints>(ce => ({
      name: ce.name,
      points: ce.sumOfPoints,
      zones: ce.sumOfZones,
      section: ce.isSection,
      userId: ce.userId,
      yearOfBirth: ce.birthYear,
      difficultyCategory: ce.category!!,
      group: (ce.competitionUserData?.group?.id as string | undefined) || "",
      favoriteRoute: ce.bestRouteId
    })).forEach(u => results.userSumOfPoints.set(u.userId, u))
  return results
}

const getNumberOfDonesPerRoute = (climbingEvents: UserClimbingEvent[]): Map<number, number> => {
  let donesPerRoute = new Map<number, number>()
  climbingEvents.map(ce => ce.climbingRoutes).flatMap(r => r).forEach(r => {
    if (isDone(r.status)) {
      if (donesPerRoute.has(r.id)) {
        donesPerRoute.set(r.id, donesPerRoute.get(r.id)!! + 1)
      } else {
        donesPerRoute.set(r.id, 1)
      }
    }
  })
  return donesPerRoute
}

const sortAndMapToPosition = (userSumOfPoints: UserSumOfPoints[], userId: string, currentUserPoints: number): PositionForSumOfPoints[] => {
  return userSumOfPoints
    .sort((u1, u2) => {
      const pointsDiff = u2.points - u1.points
      if (pointsDiff !== 0 || u1.zones) {
        return pointsDiff
      } else {
        return (u2.zones || 0) - (u1.zones || 0)
      }
    })
    .map((u, index) => u.userId === userId ? new PositionForSumOfPoints(index + 1, currentUserPoints, true) : new PositionForSumOfPoints(index + 1, u.points, false))
}